import { useEffect, useState } from 'react';
import { AlertTriangle, Check, Info, Upload } from 'lucide-react';
import type {
  CampaignMediaItem,
  CreativePreview,
  ManualCampaignForm,
} from '@/features/campaigns/types/campaign';
import { CreativeUpload } from '@/features/campaigns/components/CreativeUpload';
import type { AiPreviewOrientation } from '../AIPreview.types';

type CreativeAndLaunchSectionProps = {
  required: AiPreviewOrientation[];
  uploadOrientations: AiPreviewOrientation[];
  missingRequired: AiPreviewOrientation[];
  uploads: Partial<Record<AiPreviewOrientation, CampaignMediaItem>>;
  previewByOrientation: Partial<Record<AiPreviewOrientation, CreativePreview>>;
  onUploaded: (orientation: AiPreviewOrientation, item: CampaignMediaItem) => void;
  onCleared: (orientation: AiPreviewOrientation) => void;
  onPreviewChange: (
    orientation: AiPreviewOrientation,
    updater: CreativePreview | ((prev: CreativePreview) => CreativePreview),
  ) => void;
  dummyForm: ManualCampaignForm;
  onUploadingChange: (uploading: boolean) => void;
};

export function CreativeAndLaunchSection({
  required,
  uploadOrientations,
  missingRequired,
  uploads,
  previewByOrientation,
  onUploaded,
  onCleared,
  onPreviewChange,
  dummyForm,
  onUploadingChange,
}: CreativeAndLaunchSectionProps) {
  const [busyByOrientation, setBusyByOrientation] = useState<
    Partial<Record<AiPreviewOrientation, boolean>>
  >({});
  const [showCreativeInfo, setShowCreativeInfo] = useState(false);
  const isCreativeUploading = uploadOrientations.some(
    (orientation) => busyByOrientation[orientation],
  );
  useEffect(() => {
    onUploadingChange(isCreativeUploading);
  }, [isCreativeUploading, onUploadingChange]);

  const uploadedList = uploadOrientations.filter((orientation) => Boolean(uploads[orientation]?.mediaId));
  const someUploaded = uploadedList.length > 0;
  const lower = (list: AiPreviewOrientation[]) => list.map((o) => o.toLowerCase()).join(' and ');
  const uploadedNames = lower(uploadedList);
  const missingNames = lower(missingRequired);

  return (
    <>
      <section className="camp-section text-left">
        <div className="mb-1 flex items-center gap-2">
          <Upload className="h-4 w-4 text-[var(--dash-brand)]" />
          <h2 className="m-0 text-[15px] font-semibold text-[var(--dash-ink)]">
            Upload creatives
          </h2>
          <div className="relative">
            <button
              type="button"
              aria-label="Creative upload info"
              className="inline-flex items-center justify-center rounded-full text-[var(--dash-muted)] transition hover:text-[var(--dash-brand)]"
              onClick={() => setShowCreativeInfo((v) => !v)}
              onBlur={() => setShowCreativeInfo(false)}
            >
              <Info className="h-4 w-4" />
            </button>
            {showCreativeInfo ? (
              <div className="absolute bottom-full left-1/2 z-50 mb-2 w-56 -translate-x-1/2 rounded-xl bg-[#0F1817] px-3 py-2.5 text-xs leading-relaxed text-white shadow-lg">
                Upload your ad creative (image or video). Supported formats: MP4, JPG, PNG.
                <span className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-[#0F1817]" />
              </div>
            ) : null}
          </div>
        </div>
        <p className="mb-4 text-xs text-[var(--dash-muted)]">
          {required.length > 1
            ? 'Your plan has landscape and portrait screens. Upload content for each for the best fit, or just one and we will run it on all of them.'
            : `Upload your ${required[0]?.toLowerCase() || 'ad'} content to launch.`}
        </p>

        {someUploaded && missingRequired.length > 0 ? (
          <div className="mb-4 flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4" role="status">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="min-w-0 text-left">
              <h3 className="m-0 text-sm font-bold text-amber-950">
                Your content might not fit {missingNames} screens
              </h3>
              <p className="mt-1 mb-0 text-xs leading-relaxed text-amber-900 sm:text-sm">
                You uploaded {uploadedNames} content only, so it may not look right on {missingNames} screens. We
                recommend uploading {missingNames} content too. If you don&apos;t, we will still run your{' '}
                {uploadedNames} content on your {missingNames} screens.
              </p>
            </div>
          </div>
        ) : null}

        <div className="mb-4 flex flex-wrap gap-2">
          {uploadOrientations.map((orientation) => {
            const ready = Boolean(uploads[orientation]?.mediaId);
            const needed = !ready && someUploaded;
            return (
              <span
                key={orientation}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${ready
                  ? 'bg-[var(--dash-success-soft)] text-[var(--dash-success)]'
                  : needed
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-[var(--dash-soft)] text-[var(--dash-muted)]'
                  }`}
              >
                {ready ? (
                  <Check className="h-3 w-3" strokeWidth={3} />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-current" />
                )}
                {orientation}
                {needed ? ' recommended' : ''}
              </span>
            );
          })}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {uploadOrientations.map((orientation) => {
            const uploaded = uploads[orientation];
            const livePreview = previewByOrientation[orientation];
            const creativePreview = {
              url: livePreview?.url || uploaded?.mediaUrl || '',
              type: livePreview?.type || uploaded?.mediaType || '',
            };
            return (
              <div
                key={orientation}
                className="rounded-2xl border border-dashed border-[var(--dash-line)] bg-[var(--dash-soft)]/50 p-3"
              >
                <p className="mb-2 text-[11px] font-bold tracking-wide text-[var(--dash-muted)] uppercase">
                  {orientation} creative
                </p>
                <CreativeUpload
                  campaignData={{
                    ...dummyForm,
                    mediaUrl: uploaded?.mediaUrl || dummyForm.mediaUrl,
                    mediaType: uploaded?.mediaType || dummyForm.mediaType,
                    mediaIds: uploaded ? [uploaded] : dummyForm.mediaIds,
                  }}
                  creativePreview={creativePreview}
                  setCreativePreview={(updater) =>
                    onPreviewChange(orientation, updater)
                  }
                  hideDuration
                  forcedOrientation={orientation}
                  onUploaded={(item) => onUploaded(orientation, item)}
                  onCleared={() => onCleared(orientation)}
                  onBusyChange={(busy) =>
                    setBusyByOrientation((prev) => ({
                      ...prev,
                      [orientation]: busy,
                    }))
                  }
                />
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}
